
import React, { useState, useEffect, Suspense, lazy } from 'react';
import { HashRouter as Router, Routes, Route, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import {
  LayoutDashboard, FileText, Calendar, Truck, AlertTriangle, LogOut,
    Menu, X, Users, Pill, BookOpen, Heart, ChevronRight, ChevronLeft, Sun, Moon, Bell, Check, FolderOpen, PieChart, HelpCircle, RefreshCw, Radio, Loader2, Shield, ShieldCheck, ServerCog, Palette
} from 'lucide-react';
import OfflineIndicator from './components/OfflineIndicator';
import MajorIncidentBanner from './components/MajorIncidentBanner';
import ProtectedRoute from './components/ProtectedRoute';
import HelpModal from './components/HelpModal';
import CrewSignOnComponent from './components/CrewSignOnComponent';
import DispatchQueueComponent from './components/DispatchQueueComponent';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { DataSyncProvider } from './hooks/useDataSync';
import { ThemeProvider, useTheme, THEME_PRESETS } from './hooks/useTheme';
import { ToastProvider } from './context/ToastContext';
import { TenantProvider, useTenant } from './context/TenantContext';
import { DeviceProvider, useDevice } from './context/DeviceContext';
import { DeviceUnlockProvider } from './context/DeviceUnlockContext';
import { CrewProvider, useCrewSession } from './context/CrewContext';
import { featureFlags } from './config/featureFlags';
import { Role, AppNotification, Shift, DeviceType } from './types';
import { db } from './services/firebase';
import { collection, query, orderBy, limit, onSnapshot, updateDoc, doc, where, getDocs, Timestamp, runTransaction } from 'firebase/firestore';
import { requestBrowserPermission, sendBrowserNotification, sendNotification, notifyManagers } from './services/notificationService';
import { syncUserRecordToMirrors, userDocRef, userNotificationsCollectionRef } from './services/firestorePaths';
import ModalDialog from './components/ui/ModalDialog';
import Button from './components/ui/Button';
import { evaluateRouteAccess } from './utils/routeAccess';
import DeviceContextBanner from './components/DeviceContextBanner';
import { DEFAULT_BRAND_LOGO_URL } from './config/tenant';
import {
    getAnalyticsSessionId,
    initAnalytics,
    setRouteTrace,
    trackFeatureUsed,
    trackRuntimeError,
} from './services/analyticsService';
import { isRecoverableChunkLoadError, recoverFromChunkLoadError } from './utils/runtimeRecovery';
import { resolveWorkspaceLaunch } from './utils/workspaceResolver';
import { resolveTenantRoutingContext } from './utils/tenantRouting';
import { isAspisOperationsHost, isPlatformOperator } from './utils/platformOps';
import { DensityMode, getDensityConfig, resolveDensityMode } from './utils/uxDensity';

// Define Access Groups
const CLINICAL_ROLES = [Role.FREC4, Role.EMT, Role.Paramedic, Role.Nurse, Role.Doctor, Role.Manager, Role.Admin];
const OPS_DEVICE_TYPES: DeviceType[] = ['ambulance_tablet', 'base_station'];
const PERSONAL_DEVICE_TYPES: DeviceType[] = ['byod', 'base_station'];
const BASE_ONLY_DEVICE_TYPES: DeviceType[] = ['base_station'];

const Dashboard = lazy(() => import('./pages/Dashboard'));
const EPRFPage = lazy(() => import('./pages/EPRFPage'));
const RotaPage = lazy(() => import('./pages/RotaPage'));
const AssetPage = lazy(() => import('./pages/AssetPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const MajorIncidentPage = lazy(() => import('./pages/MajorIncidentPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const StaffPage = lazy(() => import('./pages/StaffPage'));
const DrugsPage = lazy(() => import('./pages/DrugsPage'));
const CPDPage = lazy(() => import('./pages/CPDPage'));
const WellbeingPage = lazy(() => import('./pages/WellbeingPage'));
const DocumentsPage = lazy(() => import('./pages/DocumentsPage'));
const ClinicalAnalyticsPage = lazy(() => import('./pages/ClinicalAnalyticsPage'));
const CalculatorsPage = lazy(() => import('./pages/CalculatorsPage'));
const GuidelineAssistant = lazy(() => import('./pages/GuidelineAssistant'));
const DispatchPage = lazy(() => import('./pages/DispatchPage'));
const DispatchWallboardPage = lazy(() => import('./pages/DispatchWallboardPage'));
const CrewDispatchPage = lazy(() => import('./pages/CrewDispatchPage'));
const InCabNavigationPage = lazy(() => import('./pages/InCabNavigationPage'));
const DeviceSetupPage = lazy(() => import('./pages/DeviceSetupPage'));
const HospitalHandoverPage = lazy(() => import('./pages/HospitalHandoverPage'));
const HospitalPortalView = lazy(() => import('./pages/HospitalPortalView'));
const TrustCenterPage = lazy(() => import('./pages/TrustCenterPage'));
const QuickStartSetupPage = lazy(() => import('./pages/QuickStartSetupPage'));
const PublicSetupPage = lazy(() => import('./pages/PublicSetupPage'));
const CareersPage = lazy(() => import('./pages/CareersPage'));
const ApplicationFormPage = lazy(() => import('./pages/ApplicationFormPage'));
const ApplicationPortalPage = lazy(() => import('./pages/ApplicationPortalPage'));
const RecruitmentPage = lazy(() => import('./pages/RecruitmentPage'));
const ManagerHubPage = lazy(() => import('./pages/ManagerHubPage'));
const RolesPermissionsPage = lazy(() => import('./pages/RolesPermissionsPage'));
const PlatformOpsPage = lazy(() => import('./pages/PlatformOpsPage'));

const WorkspaceLaunch = () => {
    const { user } = useAuth();
    const { tenant } = useTenant();
    const { deviceType, isRegistered } = useDevice();
    const location = useLocation();

    const params = new URLSearchParams(location.search);
    const workspaceOverride = params.get('workspace');
    const normalizedOverride =
        workspaceOverride === 'dispatch' || workspaceOverride === 'portal' || workspaceOverride === 'crew' || workspaceOverride === 'wallboard'
            ? workspaceOverride
            : undefined;

    const decision = user
        ? resolveWorkspaceLaunch({
            role: user.role,
            deviceType,
            isRegistered,
            modules: tenant.modules,
            dispatchBoardEnabled: featureFlags.dispatchBoard,
            workspaceOverride: normalizedOverride,
        })
        : null;

    useEffect(() => {
        if (!user || !decision) return;

        trackFeatureUsed('workspace_launch_resolved', {
            intent: decision.intent,
            path: decision.path,
            reason: decision.reason,
            override: normalizedOverride || null,
            tenantId: tenant.tenantId,
            role: user.role,
            deviceType,
        });
    }, [
        user,
        decision?.intent,
        decision?.path,
        decision?.reason,
        normalizedOverride,
        tenant.tenantId,
        deviceType,
    ]);

    if (!user || !decision) {
        return <Navigate to="/login" replace />;
    }

    return <Navigate to={decision.path} replace />;
};

// ... (SidebarItem component omitted, same as original)
interface SidebarItemProps {
  to: string;
  icon: any;
  label: string;
  active: boolean;
  collapsed: boolean;
  onClick?: () => void;
    emphasized?: boolean;
}

interface NavigationItem {
    to: string;
    icon: any;
    label: string;
    emphasized?: boolean;
}

interface NavigationSection {
    id: string;
    label: string;
    items: NavigationItem[];
}

const SidebarItem: React.FC<SidebarItemProps> = ({ to, icon: Icon, label, active, collapsed, onClick, emphasized }) => (
  <Link
    to={to}
    onClick={onClick}
    className={`flex items-center ${collapsed ? 'justify-center px-2' : 'space-x-2.5 px-3'} py-1.5 rounded-md transition-colors group relative mb-0.5 ${
      active
        ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
        : emphasized
          ? 'text-amber-900 dark:text-amber-200 bg-amber-50/70 dark:bg-amber-950/30 hover:bg-amber-100/80 dark:hover:bg-amber-900/30'
          : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100'
    }`}
    title={collapsed ? label : undefined}
  >
    <Icon className={`w-4 h-4 shrink-0 ${active ? 'text-white dark:text-slate-900' : ''}`} />
    {!collapsed && <span className="text-xs font-medium truncate">{label}</span>}
  </Link>
);

const getRouteDisplayMeta = (pathname: string) => {
    if (pathname === '/dashboard' || pathname === '/') {
        return {
            title: 'Operations Overview',
            description: 'Cross-workspace summary with the next operational actions for the current user.',
        };
    }

    if (pathname === '/crew-dispatch') {
        return {
            title: 'Crew MDT',
            description: 'Rapid crew operations, dispatch context, and live incident actions.',
        };
    }

    if (pathname === '/dispatch') {
        return {
            title: 'Dispatch Control',
            description: 'Live control-room queue, allocation, unit visibility, and coordination.',
        };
    }

    if (pathname === '/eprf') {
        return {
            title: 'ePRF Workspace',
            description: 'Draft retrieval, record launch, and linked clinical documentation entry.',
        };
    }

    if (pathname === '/rota') {
        return {
            title: 'Rota & Shifts',
            description: 'Scheduling, availability, staffing continuity, and timesheet actions.',
        };
    }

    if (pathname === '/documents') {
        return {
            title: 'Documents & Governance',
            description: 'Policy lifecycle, acknowledgements, governance packs, and controlled publication.',
        };
    }

    if (pathname === '/management') {
        return {
            title: 'Manager Hub',
            description: 'Leadership workspace for people, performance, and operational oversight.',
        };
    }

    if (pathname === '/staff') {
        return {
            title: 'Staff Manager',
            description: 'People records, workforce controls, reconciliation, and audit operations.',
        };
    }

    if (pathname === '/analytics') {
        return {
            title: 'Clinical Analytics',
            description: 'Performance trends, operational insight, and decision support analytics.',
        };
    }

    const fallbackTitle = pathname.substring(1).split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    return {
        title: fallbackTitle || 'Workspace',
        description: 'Operational workspace view.',
    };
};

const ThemeToggle = () => {
    const { theme, themePreset, toggleTheme, cycleThemePreset } = useTheme();
    const activePreset = THEME_PRESETS.find((preset) => preset.id === themePreset)?.label || 'Theme Preset';

    return (
        <div className="flex items-center gap-2">
            <button
                onClick={cycleThemePreset}
                className="p-2.5 rounded-xl hover:bg-white dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400 transition-all shadow-sm border border-transparent hover:border-slate-200 dark:hover:border-slate-700"
                title={`Theme Preset: ${activePreset}`}
                aria-label={`Theme Preset: ${activePreset}`}
            >
                <Palette className="w-5 h-5" />
            </button>
            <button
                onClick={toggleTheme}
                className="p-2.5 rounded-xl hover:bg-white dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400 transition-all shadow-sm border border-transparent hover:border-slate-200 dark:hover:border-slate-700"
                title="Toggle Theme"
                aria-label="Toggle Theme"
            >
                {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            </button>
        </div>
    );
};

const NotificationBell = () => {
    const { user } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const [notifications, setNotifications] = useState<AppNotification[]>([]);
    const [lastNotifiedId, setLastNotifiedId] = useState<string | null>(null);

    useEffect(() => {
        if (!user) return;
        requestBrowserPermission();

        // Query: ALL notifications, sorted by newest
        const q = query(
            userNotificationsCollectionRef(db, user.uid, user.tenantId), 
            orderBy('timestamp', 'desc'),
            limit(20)
        );

        const unsub = onSnapshot(
            q,
            (snap) => {
                const items = snap.docs.map(d => ({ id: d.id, ...d.data() } as AppNotification));
                setNotifications(items);

                // Browser Notification Logic for new items (only if unseen)
                if (items.length > 0) {
                    const latest = items[0];
                    if (latest.id !== lastNotifiedId && !latest.read) {
                        sendBrowserNotification(latest.title, latest.message);
                        setLastNotifiedId(latest.id);
                    }
                }
            },
            (error) => {
                console.warn('Notification feed unavailable', error);
                setNotifications([]);
            }
        );

        return () => unsub();
    }, [user]);

    const markAsRead = async (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!user) return;
        await updateDoc(doc(db, `users/${user.uid}/notifications`, id), { read: true });
    };

    const markAllRead = async () => {
        if (!user) return;
        // Batch update theoretically better, but simple loop fine for small numbers
        const unread = notifications.filter(n => !n.read);
        unread.forEach(n => markAsRead(n.id, { stopPropagation: () => {} } as any));
    };

    const unreadCount = notifications.filter(n => !n.read).length;

    return (
        <div className="relative">
            <button 
                onClick={() => setIsOpen(!isOpen)}
                className="p-2.5 rounded-xl hover:bg-white dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400 transition-all shadow-sm border border-transparent hover:border-slate-200 dark:hover:border-slate-700 relative"
            >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                    <span className="absolute top-2 right-2 w-2.5 h-2.5 bg-red-500 border-2 border-slate-50 dark:border-slate-900 rounded-full animate-pulse"></span>
                )}
            </button>

            {isOpen && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 z-50 overflow-hidden animate-in fade-in slide-in-from-top-2">
                    <div className="p-4 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center bg-slate-50/50 dark:bg-slate-900/50">
                        <h4 className="font-bold text-sm text-slate-800 dark:text-white">Notifications</h4>
                        {unreadCount > 0 && (
                            <button onClick={markAllRead} className="text-[10px] font-bold text-teal-600 hover:underline flex items-center gap-1">
                                <Check className="w-3 h-3" /> Mark all read
                            </button>
                        )}
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                        {notifications.length === 0 ? (
                            <div className="p-8 text-center text-slate-400 text-xs italic">
                                No new notifications.
                            </div>
                        ) : (
                            notifications.map(n => (
                                <div key={n.id} className={`p-4 border-b border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors relative group ${n.read ? 'opacity-60 bg-slate-50/50 dark:bg-slate-800/50' : 'bg-white dark:bg-slate-800'}`}>
                                    <div className="flex gap-3">
                                        <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${n.read ? 'bg-slate-300 dark:bg-slate-600' : n.type === 'alert' ? 'bg-red-500' : n.type === 'success' ? 'bg-green-500' : 'bg-blue-500'}`} />
                                        <div>
                                            <p className={`text-sm font-bold ${n.read ? 'text-slate-600 dark:text-slate-400' : 'text-slate-800 dark:text-white'}`}>{n.title}</p>
                                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">{n.message}</p>
                                            <p className="text-[10px] text-slate-400 mt-2 font-mono">{new Date(n.timestamp).toLocaleString()}</p>
                                        </div>
                                    </div>
                                    {!n.read && (
                                        <button 
                                            onClick={(e) => markAsRead(n.id, e)}
                                            className="absolute top-2 right-2 p-1 text-slate-300 hover:text-teal-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                            title="Mark as Read"
                                        >
                                            <Check className="w-3 h-3" />
                                        </button>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// Hook for passive notifications (Reminders, Compliance, Manager Alerts)
const useNotificationLogic = () => {
    const { user } = useAuth();
    const isManager = user?.role === Role.Manager || user?.role === Role.Admin;

    useEffect(() => {
        if (!user || !user.tenantId) return;

        const checkRoutine = async () => {
            const now = new Date();
            const nowStr = now.toDateString(); // Day granularity for compliance

            // 1. Shift Reminders (1 hour before)
            // Query shifts starting between now and now + 65 mins
            const startWindow = new Date(now.getTime());
            const endWindow = new Date(now.getTime() + 65 * 60 * 1000); // 65 mins window
            
            try {
                const qShifts = query(
                    collection(db, 'shifts'),
                    where('tenantId', '==', user.tenantId),
                    where('start', '>=', Timestamp.fromDate(startWindow)),
                    where('start', '<=', Timestamp.fromDate(endWindow))
                );
                
                const snap = await getDocs(qShifts);
                snap.docs.forEach(d => {
                    const data = d.data();
                    const shift = { 
                        ...data, 
                        id: d.id, 
                        start: data.start.toDate(), 
                        end: data.end.toDate() 
                    } as Shift;

                    // Check if I am on this shift
                    if (shift.slots.some(s => s.userId === user.uid)) {
                        const storageKey = `reminder_sent_${d.id}`;
                        if (!localStorage.getItem(storageKey)) {
                            // Send notification
                            sendNotification(user.uid, "Upcoming Shift", `You have a shift at ${shift.location} starting soon (${shift.start.toLocaleTimeString()}).`, 'info', '/rota');
                            sendBrowserNotification("Shift Reminder", `Shift at ${shift.location} starts in <1 hour.`);
                            localStorage.setItem(storageKey, 'true');
                        }
                    }
                });
            } catch (e) { console.error("Shift reminder check failed", e); }

            // 2. Compliance Check (Once per day)
            const compCheckKey = `comp_check_${user.uid}_${nowStr}`;
            if (!localStorage.getItem(compCheckKey)) {
                user.compliance?.forEach(doc => {
                    if (doc.expiryDate) {
                        const exp = new Date(doc.expiryDate);
                        const diffTime = exp.getTime() - now.getTime();
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                        if (diffDays < 30) {
                            sendNotification(user.uid, "Compliance Expiry", `Your ${doc.name} expires in ${diffDays} days.`, 'alert', '/profile');
                        }
                    }
                });
                localStorage.setItem(compCheckKey, 'true');
            }

            // 3. Manager Alerts (Uncovered Shifts & Failed Clock Outs)
            if (isManager) {
                const mgrCheckKey = `mgr_check_${now.getHours()}`; // Once per hour roughly
                if (!localStorage.getItem(mgrCheckKey)) {
                    // Check Uncovered Shifts < 24h
                    const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
                    const qUncovered = query(
                        collection(db, 'shifts'),
                        where('tenantId', '==', user.tenantId),
                        where('start', '>=', Timestamp.fromDate(now)),
                        where('start', '<=', Timestamp.fromDate(tomorrow))
                    );
                    const snapUncov = await getDocs(qUncovered);
                    let uncoveredCount = 0;
                    snapUncov.docs.forEach(d => {
                        const s = d.data() as Shift;
                        if (s.status !== 'Cancelled' && s.slots.some(slot => !slot.userId)) uncoveredCount++;
                    });
                    
                    if (uncoveredCount > 0) {
                        sendNotification(user.uid, "Staffing Alert", `${uncoveredCount} shifts in the next 24h have open slots.`, 'alert', '/rota');
                    }

                    // Check Failed Clock Outs (Shift ended > 4 hours ago but no clock out)
                    const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
                    const fourHoursAgo = new Date(now.getTime() - 4 * 60 * 60 * 1000);
                    const qFailed = query(
                        collection(db, 'shifts'),
                        where('tenantId', '==', user.tenantId),
                        where('end', '>=', Timestamp.fromDate(yesterday)),
                        where('end', '<=', Timestamp.fromDate(fourHoursAgo))
                    );
                    const snapFailed = await getDocs(qFailed);
                    let failedCount = 0;
                    snapFailed.docs.forEach(d => {
                        const s = d.data() as Shift;
                        if (s.timeRecords) {
                            Object.values(s.timeRecords).forEach(tr => {
                                if (tr.clockInTime && !tr.clockOutTime) failedCount++;
                            });
                        }
                    });

                    if (failedCount > 0) {
                        sendNotification(user.uid, "Timesheet Alert", `${failedCount} staff members failed to clock out from yesterday's shifts.`, 'alert', '/rota');
                    }

                    localStorage.setItem(mgrCheckKey, 'true');
                }
            }
        };

        const interval = setInterval(checkRoutine, 60000); // Check every minute
        checkRoutine(); // Run immediately

        return () => clearInterval(interval);
    }, [user, isManager]);
};

const MainLayout = ({ children }: { children?: React.ReactNode }) => {
    const { user, logout } = useAuth();
        const { tenant } = useTenant();
        const { deviceType, deviceLabel, isRegistered } = useDevice();
    const location = useLocation();
    const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
    const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
    const [activeShiftAtSignOut, setActiveShiftAtSignOut] = useState<Shift | null>(null);
    const [signOutCheckLoading, setSignOutCheckLoading] = useState(false);
    const [signOutProcessing, setSignOutProcessing] = useState(false);
    const [signOutError, setSignOutError] = useState('');
    const [densityMode, setDensityMode] = useState<DensityMode>(() => {
        if (typeof window === 'undefined') return 'balanced';
        return resolveDensityMode({
            viewportWidth: window.innerWidth,
            isTouch: window.matchMedia('(pointer: coarse)').matches,
        });
    });

  useEffect(() => {
      if (typeof window === 'undefined') return;
      const updateDensityMode = () => {
          setDensityMode(resolveDensityMode({
              viewportWidth: window.innerWidth,
              isTouch: window.matchMedia('(pointer: coarse)').matches,
          }));
      };

      updateDensityMode();
      window.addEventListener('resize', updateDensityMode);
      return () => window.removeEventListener('resize', updateDensityMode);
  }, []);

  const densityConfig = getDensityConfig(densityMode);

  // Invoke Notification Logic Hook
  useNotificationLogic();

    const isClinical = user ? CLINICAL_ROLES.includes(user.role) : false;
    const isManager = user?.role === Role.Manager || user?.role === Role.Admin;
    const isEPRF = location.pathname === '/eprf';
    const isContainedShell = location.pathname === '/dispatch' || location.pathname === '/crew-dispatch';
    const { modules } = tenant;

    // Device-aware nav now uses the same role/device/module evaluator as route guards.
    const isAmbulance = deviceType === 'ambulance_tablet';
    const canAccess = (options: {
        allowedRoles?: Role[];
        allowedDeviceTypes?: DeviceType[];
        requiredModules?: Array<keyof typeof modules>;
    }) => {
        if (!user) return false;
        return evaluateRouteAccess({
            role: user.role,
            deviceType,
            isRegistered,
            modules,
            allowedRoles: options.allowedRoles,
            allowedDeviceTypes: options.allowedDeviceTypes,
            requiredModules: options.requiredModules,
        }).allowed;
    };

    const showClinicalOps = canAccess({ allowedDeviceTypes: OPS_DEVICE_TYPES, requiredModules: ['clinicalOps'] });
    const showPersonal = canAccess({ allowedDeviceTypes: PERSONAL_DEVICE_TYPES, requiredModules: ['coreOps'] });
    const showFleetAndStock = canAccess({ allowedDeviceTypes: OPS_DEVICE_TYPES, requiredModules: ['clinicalOps', 'fleetAndStock'] });
    const showDispatch = featureFlags.dispatchBoard && canAccess({ allowedDeviceTypes: OPS_DEVICE_TYPES, requiredModules: ['clinicalOps', 'dispatch'] });
    const showMajorIncident = canAccess({ allowedDeviceTypes: OPS_DEVICE_TYPES, requiredModules: ['majorIncident'] });
    const showWallboard = canAccess({ allowedRoles: [Role.Manager, Role.Admin], allowedDeviceTypes: BASE_ONLY_DEVICE_TYPES, requiredModules: ['clinicalOps', 'dispatch'] });
    const showAnalytics = canAccess({
        allowedRoles: [Role.Manager, Role.Admin],
        allowedDeviceTypes: BASE_ONLY_DEVICE_TYPES,
        requiredModules: ['analytics'],
    });
    const showTrustCenter = canAccess({ requiredModules: ['trustCenter'] });
    const showManagement = canAccess({ allowedRoles: [Role.Manager, Role.Admin], allowedDeviceTypes: BASE_ONLY_DEVICE_TYPES });
    const onOpsHost = typeof window !== 'undefined' ? isAspisOperationsHost(window.location.hostname) : false;
    const showPlatformOps = showManagement && onOpsHost && isPlatformOperator(user?.email);
    const isDemoStage = tenant.environmentStage !== 'production';

    const workspaceSwitchOptions = [
        showDispatch ? { value: '/dispatch', label: 'Dispatch Control' } : null,
        showClinicalOps ? { value: '/crew-dispatch', label: 'Crew MDT' } : null,
        showPersonal ? { value: '/rota', label: 'Personal Portal' } : null,
        showManagement ? { value: '/management', label: 'Manager Hub' } : null,
        showWallboard ? { value: '/wallboard', label: 'Wallboard' } : null,
    ].filter(Boolean) as Array<{ value: string; label: string }>;

    const currentWorkspaceValue =
        workspaceSwitchOptions.find((option) =>
            option.value === '/dispatch' || option.value === '/crew-dispatch' || option.value === '/rota' || option.value === '/management' || option.value === '/wallboard'
                ? location.pathname.startsWith(option.value)
                : location.pathname === option.value
        )?.value || '';
    const routeMeta = getRouteDisplayMeta(location.pathname);
    const navigationSections: NavigationSection[] = [
        {
            id: 'overview',
            label: 'Overview',
            items: [
                { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
            ],
        },
        ...(showClinicalOps ? [{
            id: 'clinical-ops',
            label: 'Clinical Operations',
            items: [
                { to: '/eprf', icon: FileText, label: 'ePRF Records' },
                { to: '/crew-dispatch', icon: Radio, label: 'Crew Dispatch' },
                ...(showDispatch ? [{ to: '/dispatch', icon: Radio, label: 'Dispatch Board' }] : []),
                ...(showFleetAndStock ? [{ to: '/assets', icon: Truck, label: 'Assets & Fleet' }] : []),
                ...(showFleetAndStock && isClinical ? [{ to: '/drugs', icon: Pill, label: 'Drugs Register' }] : []),
                ...(isAmbulance && showMajorIncident ? [{ to: '/major-incident', icon: AlertTriangle, label: 'Major Incident' }] : []),
            ],
        }] : []),
        ...(showPersonal ? [{
            id: 'people-schedule',
            label: 'People & Scheduling',
            items: [
                { to: '/rota', icon: Calendar, label: 'Rota & Shifts' },
                { to: '/cpd', icon: BookOpen, label: 'CPD Portfolio' },
                { to: '/wellbeing', icon: Heart, label: 'Wellbeing Hub' },
            ],
        }] : []),
        {
            id: 'governance',
            label: 'Governance & Documents',
            items: [
                { to: '/documents', icon: FolderOpen, label: 'Documents' },
                ...(showTrustCenter ? [{ to: '/trust', icon: ShieldCheck, label: 'Trust Center' }] : []),
            ],
        },
        ...(showManagement ? [{
            id: 'administration',
            label: 'Administration',
            items: [
                { to: '/quick-start', icon: Check, label: 'Quick Start Setup', emphasized: isDemoStage },
                { to: '/management', icon: Users, label: 'Manager Hub' },
                { to: '/roles', icon: Shield, label: 'Roles & Permissions' },
                ...(showPlatformOps ? [{ to: '/platform-ops', icon: ServerCog, label: 'Platform Ops' }] : []),
                { to: '/staff', icon: Users, label: 'Staff Manager' },
                { to: '/recruitment', icon: Users, label: 'Recruitment' },
                ...(showWallboard ? [{ to: '/wallboard', icon: Radio, label: 'Wallboard' }] : []),
                ...(showAnalytics ? [{ to: '/analytics', icon: PieChart, label: 'Clinical Analytics' }] : []),
                ...(!isAmbulance && showMajorIncident ? [{ to: '/major-incident', icon: AlertTriangle, label: 'Major Incident' }] : []),
            ],
        }] : []),
    ].filter((section) => section.items.length > 0);

  useEffect(() => {
      const requestPermissions = async () => {
          try {
              requestBrowserPermission();
          } catch (e) {
              console.log("Permission request cycle partial or failed", e);
          }
      };
      requestPermissions();
  }, []);

  useEffect(() => {
      if (!user) return;

      initAnalytics({
          tenantId: user.tenantId || 'unknown-tenant',
          userId: user.uid,
          role: user.role,
          deviceType,
          sessionId: getAnalyticsSessionId(),
      });
  }, [deviceType, user]);

  useEffect(() => {
      if (!user) return;
      setRouteTrace(location.pathname);
  }, [location.pathname, user]);

  useEffect(() => {
      if (!user) return;

      const handleWindowError = (event: ErrorEvent) => {
          trackRuntimeError(location.pathname, 'WindowError', {
              message: event.message || 'Unknown window error',
              source: event.filename || 'window.error',
              line: event.lineno || null,
              column: event.colno || null,
          });
      };

      const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
          const reason = event.reason instanceof Error
              ? event.reason.message
              : typeof event.reason === 'string'
                  ? event.reason
                  : 'Unhandled promise rejection';

          trackRuntimeError(location.pathname, 'UnhandledRejection', {
              message: reason,
              source: 'window.unhandledrejection',
          });

          if (isRecoverableChunkLoadError(event.reason)) {
              recoverFromChunkLoadError().catch((error) => {
                  console.warn('Chunk recovery from unhandled rejection failed', error);
              });
          }
      };

      window.addEventListener('error', handleWindowError);
      window.addEventListener('unhandledrejection', handleUnhandledRejection);

      return () => {
          window.removeEventListener('error', handleWindowError);
          window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      };
  }, [location.pathname, user]);

  useEffect(() => {
      if (isEPRF) setCollapsed(true);
  }, [isEPRF]);

  const findActiveShift = async (): Promise<Shift | null> => {
      if (!user || !user.tenantId) return null;
      const now = new Date();
      const start = new Date(now);
      start.setDate(start.getDate() - 2);
      const end = new Date(now);
      end.setDate(end.getDate() + 2);
      const q = query(
          collection(db, 'shifts'),
          where('tenantId', '==', user.tenantId),
          where('start', '>=', Timestamp.fromDate(start)),
          where('start', '<=', Timestamp.fromDate(end))
      );
      const snap = await getDocs(q);
      const shifts = snap.docs.map(d => ({
          id: d.id,
          ...d.data(),
          start: d.data().start.toDate(),
          end: d.data().end.toDate()
      } as Shift));
      return shifts.find(s => {
          const rec = s.timeRecords?.[user.uid];
          return !!(rec && rec.clockInTime && !rec.clockOutTime);
      }) || null;
  };

  const handleSignOutRequest = async () => {
      if (!user) {
          await logout();
          return;
      }
      setMobileMenuOpen(false);
      setShowSignOutConfirm(true);
      setSignOutError('');
      setSignOutCheckLoading(true);
      try {
          const activeShift = await findActiveShift();
          setActiveShiftAtSignOut(activeShift);
      } catch (err) {
          console.error('Active shift check failed', err);
          setSignOutError('Could not verify shift status. You can still sign out, but shift state may be stale.');
          setActiveShiftAtSignOut(null);
      } finally {
          setSignOutCheckLoading(false);
      }
  };

  const handleSignOutOnly = async () => {
      setSignOutProcessing(true);
      try {
          await logout();
          setShowSignOutConfirm(false);
      } finally {
          setSignOutProcessing(false);
      }
  };

  const handleEndShiftAndSignOut = async () => {
      if (!user || !activeShiftAtSignOut) {
          await handleSignOutOnly();
          return;
      }
      setSignOutProcessing(true);
      setSignOutError('');
      try {
          await runTransaction(db, async (transaction) => {
              const shiftRef = doc(db, 'shifts', activeShiftAtSignOut.id);
              const userRef = userDocRef(db, user.uid, user.tenantId);
              const shiftDoc = await transaction.get(shiftRef);
              const userDoc = await transaction.get(userRef);
              if (!shiftDoc.exists() || !userDoc.exists()) throw new Error('Unable to complete handover.');
              const shiftData = shiftDoc.data() as Shift;
              const userData = userDoc.data() as any;
              const timeRecord = shiftData.timeRecords?.[user.uid];
              if (!timeRecord || !timeRecord.clockInTime || timeRecord.clockOutTime) {
                  return;
              }
              const outTime = new Date().toISOString();
              const durationMs = new Date(outTime).getTime() - new Date(timeRecord.clockInTime).getTime();
              const durationHours = durationMs / (1000 * 60 * 60);
              const newTimeRecord = {
                  ...timeRecord,
                  clockOutTime: outTime,
                  durationMinutes: Math.round(durationMs / 60000)
              };
              transaction.update(shiftRef, { [`timeRecords.${user.uid}`]: newTimeRecord });
              const currentStats = userData.stats || { totalHours: 0, completedShifts: 0 };
              transaction.update(userRef, {
                  stats: {
                      totalHours: (currentStats.totalHours || 0) + durationHours,
                      completedShifts: (currentStats.completedShifts || 0) + 1,
                      lastShiftDate: outTime
                  }
              });
          });
          await syncUserRecordToMirrors(db, user.uid, user.tenantId);
          await logout();
          setShowSignOutConfirm(false);
      } catch (err) {
          console.error('End-shift sign-out failed', err);
          setSignOutError('Unable to end shift automatically. You can sign out only, or retry.');
      } finally {
          setSignOutProcessing(false);
      }
  };

  return (
        <div className="flex h-screen bg-transparent transition-colors duration-300">
      {/* Sidebar - Desktop */}
            <aside className={`hidden md:flex flex-col bg-[color:var(--surface)]/95 dark:bg-[color:var(--surface)]/95 border-r border-slate-200/70 dark:border-slate-700/70 backdrop-blur-md transition-all duration-300 ${collapsed ? 'w-14' : 'w-52'} z-40`}>
        <div className="px-3 py-3 flex items-center justify-between">
          {!collapsed && (
            <div className="flex items-center gap-2 animate-in fade-in">
                                                                <img
                                                                    src={tenant.branding.logoUrl}
                                                                    alt="Logo"
                                                                    className="h-8 w-auto object-contain"
                                                                    onError={(event) => {
                                                                        event.currentTarget.onerror = null;
                                                                        event.currentTarget.src = DEFAULT_BRAND_LOGO_URL;
                                                                    }}
                                                                />
                                <span className="font-bold text-lg text-slate-800 dark:text-white tracking-tight">{tenant.branding.shortName}</span>
            </div>
          )}
                                        {collapsed && (
                                            <img
                                                src={tenant.branding.logoUrl}
                                                alt="Logo"
                                                className="h-8 w-auto mx-auto"
                                                onError={(event) => {
                                                    event.currentTarget.onerror = null;
                                                    event.currentTarget.src = DEFAULT_BRAND_LOGO_URL;
                                                }}
                                            />
                                        )}
          <button onClick={() => setCollapsed(!collapsed)} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 transition-colors border border-transparent hover:border-slate-200 dark:hover:border-slate-700">
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>

                <nav className="flex-1 px-2 space-y-3 overflow-y-auto no-scrollbar">
                    {navigationSections.map((section) => (
                        <div key={section.id} className="space-y-0.5">
                            {!collapsed && (
                                <p className="px-2 pt-2 text-[9px] font-bold uppercase tracking-[0.16em] text-slate-400 dark:text-slate-500">
                                    {section.label}
                                </p>
                            )}
                            <div className="space-y-1">
                                {section.items.map((item) => (
                                    <SidebarItem
                                        key={item.to}
                                        to={item.to}
                                        icon={item.icon}
                                        label={item.label}
                                        active={location.pathname === item.to}
                                        collapsed={collapsed}
                                        emphasized={item.emphasized}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                </nav>

                <div className="px-2 py-2 border-t border-slate-200 dark:border-slate-800 space-y-0.5">
                    {!collapsed && (
                        <div className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs font-medium mb-1 ${
                            isRegistered
                                ? 'bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-400'
                                : 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400'
                        }`}>
                            <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${isRegistered ? 'bg-teal-500' : 'bg-amber-400'}`} />
                            <span className="truncate">{isRegistered ? deviceLabel : 'BYOD'}</span>
                        </div>
                    )}
                    {isManager && !collapsed && !isRegistered && (
                        <Link to="/device-setup" className="flex items-center gap-2 px-2 py-1.5 text-xs font-medium text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded transition-colors w-full">
                            <Truck className="w-3.5 h-3.5 shrink-0" /> Register Device
                        </Link>
                    )}
                    <button onClick={() => setShowHelp(true)} className={`flex items-center ${collapsed ? 'justify-center' : 'gap-2 px-2'} w-full py-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition-colors text-xs`}>
                            <HelpCircle className="w-3.5 h-3.5" />
                            {!collapsed && <span>Help</span>}
                    </button>
                    <button onClick={handleSignOutRequest} className={`flex items-center ${collapsed ? 'justify-center' : 'gap-2 px-2'} w-full py-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors text-xs`}>
                            <LogOut className="w-3.5 h-3.5" />
                            {!collapsed && <span>Sign Out</span>}
                    </button>
                    {!collapsed && (
                        <p className="px-3 pt-1 text-[10px] leading-relaxed text-slate-400 dark:text-slate-500">
                            {tenant.branding.legalAttribution}
                        </p>
                    )}
                </div>
      </aside>

      {/* Mobile Header with Safe Area Padding */}
    <div className="md:hidden fixed top-0 w-full bg-[color:var(--surface)]/90 backdrop-blur-md border-b border-slate-200/70 dark:border-slate-700/70 z-50 px-4 pt-[env(safe-area-inset-top)] transition-all duration-300">
          <div className="h-16 flex items-center justify-between">
              <div className="flex items-center gap-3">
                  <button onClick={() => setMobileMenuOpen(true)} className="p-2 -ml-2 text-slate-600 dark:text-slate-300">
                      <Menu className="w-6 h-6" />
                  </button>
                  <span className="font-bold text-lg text-slate-800 dark:text-white">{tenant.branding.shortName}</span>
              </div>
              <div className="flex items-center gap-2">
                  <ThemeToggle />
                  <Link to="/profile" className="w-8 h-8 bg-teal-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                      {user?.name.charAt(0)}
                  </Link>
              </div>
          </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
          <div className="fixed inset-0 z-[60] bg-slate-900/50 backdrop-blur-sm md:hidden" onClick={() => setMobileMenuOpen(false)}>
              <div className="absolute left-0 top-0 bottom-0 w-64 bg-[color:var(--surface)] shadow-2xl p-4 pt-[calc(1rem+env(safe-area-inset-top))] pb-[calc(1rem+env(safe-area-inset-bottom))] flex flex-col" onClick={e => e.stopPropagation()}>
                  <div className="flex justify-between items-center mb-6">
                      <span className="font-bold text-xl text-slate-800 dark:text-white">Menu</span>
                      <button onClick={() => setMobileMenuOpen(false)}><X className="w-6 h-6 text-slate-500" /></button>
                  </div>
                                    <nav className="space-y-4 flex-1 overflow-y-auto no-scrollbar">
                                        {navigationSections.map((section) => (
                                            <div key={section.id} className="space-y-1.5">
                                                <p className="px-4 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400 dark:text-slate-500">
                                                    {section.label}
                                                </p>
                                                <div className="space-y-1">
                                                    {section.items.map((item) => (
                                                        <SidebarItem
                                                            key={item.to}
                                                            to={item.to}
                                                            icon={item.icon}
                                                            label={item.label}
                                                            active={location.pathname === item.to}
                                                            collapsed={false}
                                                            emphasized={item.emphasized}
                                                            onClick={() => setMobileMenuOpen(false)}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </nav>
                  
                  <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800 space-y-2">
                      <button onClick={() => { setShowHelp(true); setMobileMenuOpen(false); }} className="flex items-center gap-3 px-4 py-3 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-xl font-bold text-sm w-full">
                          <HelpCircle className="w-5 h-5" /> Help & Support
                      </button>
                      <button onClick={handleSignOutRequest} className="flex items-center gap-3 px-4 py-3 bg-red-50 dark:bg-red-900/20 text-red-600 rounded-xl font-bold text-sm w-full">
                          <LogOut className="w-5 h-5" /> Sign Out
                      </button>
                  </div>
              </div>
          </div>
      )}

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col h-full overflow-hidden relative pt-[calc(4rem+env(safe-area-inset-top))] md:pt-0">
          <MajorIncidentBanner />
          <OfflineIndicator />
          
          {/* Desktop Top Bar - Hide on EPRF for workspace focus */}
          {!isEPRF && (
            <header className="hidden md:flex items-center justify-between px-4 py-2 bg-[color:var(--surface)]/70 backdrop-blur-sm border-b border-slate-200/70 dark:border-slate-700/70 sticky top-0 z-30 min-h-[42px]">
                <div className="flex items-center gap-3">
                    <h2 className="text-sm font-semibold text-slate-900 dark:text-white">{routeMeta.title}</h2>
                    {!isRegistered && (
                        <span className="inline-flex items-center rounded px-1.5 py-0.5 bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300 text-[10px] font-bold uppercase">BYOD</span>
                    )}
                    {tenant.environmentStage !== 'production' && (
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${
                            tenant.environmentStage === 'demo'
                                ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300'
                                : 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300'
                        }`}>{tenant.environmentStage}</span>
                    )}
                    {workspaceSwitchOptions.length > 1 && (
                        <select
                            value={currentWorkspaceValue}
                            onChange={(event) => { if (event.target.value) navigate(event.target.value); }}
                            className="bg-transparent text-xs font-medium text-slate-600 dark:text-slate-300 focus:outline-none border border-slate-200 dark:border-slate-700 rounded px-1.5 py-0.5"
                            aria-label="Switch workspace"
                        >
                            {workspaceSwitchOptions.map((option) => (
                                <option key={option.value} value={option.value} className="text-slate-900">{option.label}</option>
                            ))}
                        </select>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <p className="text-[11px] text-slate-400 dark:text-slate-500 hidden xl:block">
                        {new Date().toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                    </p>
                    <button onClick={() => window.location.reload()} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 transition-colors" title="Force Refresh">
                        <RefreshCw className="w-3.5 h-3.5" />
                    </button>
                    <ThemeToggle />
                    <NotificationBell />
                    <Link to="/profile" className="flex items-center gap-2 pl-3 border-l border-slate-200 dark:border-slate-700 group cursor-pointer">
                        <div className="text-right hidden lg:block">
                            <p className="text-xs font-semibold text-slate-700 dark:text-white">{user?.name}</p>
                            <p className="text-[10px] text-slate-400 dark:text-slate-500">{user?.role}</p>
                        </div>
                        <div className="w-7 h-7 rounded-full border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 flex items-center justify-center text-slate-700 dark:text-slate-100 text-xs font-bold overflow-hidden">
                            {user?.photoURL ? (
                                <img src={user.photoURL} alt={user.name} className="w-full h-full object-cover" />
                            ) : (
                                user?.name.charAt(0)
                            )}
                        </div>
                    </Link>
                </div>
            </header>
          )}

          {/* Device context banner — shows on BYOD when clinical features are limited */}
          {!isEPRF && <DeviceContextBanner />}

          {!isEPRF && isDemoStage && (
            <div className="px-4 md:px-8 pt-4">
                <div className="max-w-7xl mx-auto rounded-xl border border-amber-300/80 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 px-4 py-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                        <p className="text-xs font-bold uppercase tracking-wide text-amber-800 dark:text-amber-300">{tenant.environmentStage === 'demo' ? 'Demo Mode Active' : 'Pre-Production Mode Active'}</p>
                        <p className="text-sm text-amber-900 dark:text-amber-200">
                            This workspace is not yet in production. Complete Quick Start onboarding and production checks before making major operational changes.
                        </p>
                    </div>
                    {showManagement ? (
                        <Link
                            to="/quick-start"
                            className="inline-flex items-center justify-center rounded-lg bg-amber-700 hover:bg-amber-800 text-white text-sm font-semibold px-4 py-2"
                        >
                            Open Quick Start
                        </Link>
                    ) : (
                        <span className="text-xs font-semibold text-amber-800 dark:text-amber-300">
                            Ask your manager/admin to complete Quick Start and promote this tenant to production.
                        </span>
                    )}
                </div>
            </div>
          )}

          <div className={`flex-1 ${isContainedShell ? 'overflow-hidden' : 'overflow-y-auto'} ${isEPRF || isContainedShell ? 'p-0' : densityConfig.pagePadding === 'sm' ? 'p-2 md:p-3' : densityConfig.pagePadding === 'lg' ? 'p-4 md:p-5' : 'p-3 md:p-4'} ${densityConfig.shellGap} scroll-smooth pb-safe`}>
              <div className={isContainedShell ? 'h-full' : 'max-w-7xl mx-auto h-full'}>
                  {children}
              </div>
          </div>
      </main>

      {/* Global Modals */}
      {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}

      {showSignOutConfirm && (
          <ModalDialog title="Sign Out" onClose={() => !signOutProcessing && setShowSignOutConfirm(false)} className="z-[80]">
              <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 w-full max-w-md border border-slate-200 dark:border-slate-700 space-y-5">
                  {signOutCheckLoading ? (
                      <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Checking active shift status...
                      </div>
                  ) : (
                      <>
                          {activeShiftAtSignOut ? (
                              <p className="text-sm text-slate-700 dark:text-slate-300">
                                  You are still clocked in at <span className="font-bold">{activeShiftAtSignOut.location}</span>. For shared-device handover, end shift before signing out.
                              </p>
                          ) : (
                              <p className="text-sm text-slate-700 dark:text-slate-300">
                                  Sign out from this device now? Use this at handover so the next crew member logs in with their own account.
                              </p>
                          )}
                          {signOutError && <p className="text-xs text-red-600 dark:text-red-400 font-semibold">{signOutError}</p>}
                          <div className="flex items-center justify-end gap-3">
                              <Button variant="ghost" disabled={signOutProcessing} onClick={() => setShowSignOutConfirm(false)}>Cancel</Button>
                              {activeShiftAtSignOut && (
                                  <Button
                                      variant="danger"
                                      disabled={signOutProcessing}
                                      onClick={handleEndShiftAndSignOut}
                                  >
                                      {signOutProcessing ? 'Processing...' : 'End Shift + Sign Out'}
                                  </Button>
                              )}
                              <Button
                                  variant={activeShiftAtSignOut ? 'secondary' : 'primary'}
                                  disabled={signOutProcessing}
                                  onClick={handleSignOutOnly}
                              >
                                  {signOutProcessing ? 'Signing Out...' : 'Sign Out Only'}
                              </Button>
                          </div>
                      </>
                  )}
              </div>
          </ModalDialog>
      )}
    </div>
  );
};

const App = () => {
    const routingContext = typeof window !== 'undefined'
        ? resolveTenantRoutingContext({
            hostname: window.location.hostname,
            pathname: window.location.pathname,
            baseDomain: 'aspis.software',
        })
        : { isSetupHost: false, source: 'default' as const, compatibilityMode: true };

    const isSetupHost = routingContext.isSetupHost;

    const routeFallback = (
        <div className="min-h-[40vh] flex items-center justify-center">
            <div className="flex items-center gap-3 rounded-xl bg-white/80 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 px-4 py-3 text-slate-700 dark:text-slate-200">
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span className="text-sm font-semibold">Loading workspace...</span>
            </div>
        </div>
    );


    return (
        <Router>
            <ThemeProvider>
                <AuthProvider>
                    <TenantProvider>
                        <ToastProvider>
                            <DataSyncProvider>
                                <DeviceProvider>
                                    <DeviceUnlockProvider>
                                    <CrewProvider>
                                        <Suspense fallback={routeFallback}>
                                        <Routes>
                                            <Route path="/setup" element={<PublicSetupPage />} />
                                            <Route path="/setup/:tenantSlug" element={<PublicSetupPage />} />
                                            <Route path="/careers" element={<CareersPage />} />
                                            <Route path="/apply" element={<ApplicationFormPage />} />
                                            <Route path="/apply/:jobId" element={<ApplicationFormPage />} />
                                            <Route path="/applications/:portalAccessKey" element={<ApplicationPortalPage />} />
                                            <Route path="/login" element={<LoginPage />} />
                                            <Route
                                                path="/"
                                                element={
                                                    isSetupHost
                                                        ? <Navigate to="/setup" replace />
                                                        : <ProtectedRoute><WorkspaceLaunch /></ProtectedRoute>
                                                }
                                            />
                                            <Route path="/dashboard" element={<ProtectedRoute><MainLayout><Dashboard /></MainLayout></ProtectedRoute>} />
                                            <Route
                                                path="/t/:tenantSlug/*"
                                                element={
                                                    isSetupHost
                                                        ? <Navigate to="/setup" replace />
                                                        : <ProtectedRoute><WorkspaceLaunch /></ProtectedRoute>
                                                }
                                            />
                                            <Route path="/eprf" element={<ProtectedRoute allowedDeviceTypes={OPS_DEVICE_TYPES} requiredModules={['clinicalOps']}><MainLayout><EPRFPage /></MainLayout></ProtectedRoute>} />
                                            <Route path="/crew-dispatch" element={<ProtectedRoute allowedDeviceTypes={OPS_DEVICE_TYPES} requiredModules={['clinicalOps', 'dispatch']}><MainLayout><CrewDispatchPage /></MainLayout></ProtectedRoute>} />
                                            <Route path="/crew/navigate/:assignmentId" element={<ProtectedRoute allowedDeviceTypes={['ambulance_tablet', 'byod']} requiredModules={['clinicalOps', 'dispatch']}><InCabNavigationPage /></ProtectedRoute>} />
                                            {featureFlags.dispatchBoard && (
                                                <Route path="/dispatch" element={<ProtectedRoute allowedDeviceTypes={OPS_DEVICE_TYPES} requiredModules={['clinicalOps', 'dispatch']}><MainLayout><DispatchPage /></MainLayout></ProtectedRoute>} />
                                            )}
                                            {featureFlags.dispatchBoard && (
                                                <Route
                                                    path="/wallboard"
                                                    element={
                                                        <ProtectedRoute
                                                            allowedRoles={[Role.Manager, Role.Admin]}
                                                            allowedDeviceTypes={BASE_ONLY_DEVICE_TYPES}
                                                            requiredModules={['clinicalOps', 'dispatch']}
                                                        >
                                                            <DispatchWallboardPage />
                                                        </ProtectedRoute>
                                                    }
                                                />
                                            )}
                                            <Route path="/rota" element={<ProtectedRoute allowedDeviceTypes={PERSONAL_DEVICE_TYPES} requiredModules={['coreOps']}><MainLayout><RotaPage /></MainLayout></ProtectedRoute>} />
                                            <Route path="/assets" element={<ProtectedRoute allowedDeviceTypes={OPS_DEVICE_TYPES} requiredModules={['clinicalOps', 'fleetAndStock']}><MainLayout><AssetPage /></MainLayout></ProtectedRoute>} />
                                            <Route path="/drugs" element={<ProtectedRoute allowedRoles={CLINICAL_ROLES} allowedDeviceTypes={OPS_DEVICE_TYPES} requiredModules={['clinicalOps', 'fleetAndStock']}><MainLayout><DrugsPage /></MainLayout></ProtectedRoute>} />
                                            <Route path="/staff" element={<ProtectedRoute allowedRoles={[Role.Manager, Role.Admin]} allowedDeviceTypes={BASE_ONLY_DEVICE_TYPES} requiredPermission={{ module: 'PeopleAndRecruitment', action: 'Administer' }}><MainLayout><StaffPage /></MainLayout></ProtectedRoute>} />
                                            <Route path="/recruitment" element={<ProtectedRoute allowedRoles={[Role.Manager, Role.Admin]} allowedDeviceTypes={BASE_ONLY_DEVICE_TYPES} requiredPermission={{ module: 'PeopleAndRecruitment', action: 'Administer' }}><MainLayout><RecruitmentPage /></MainLayout></ProtectedRoute>} />
                                            <Route path="/management" element={<ProtectedRoute allowedRoles={[Role.Manager, Role.Admin]} allowedDeviceTypes={BASE_ONLY_DEVICE_TYPES}><MainLayout><ManagerHubPage /></MainLayout></ProtectedRoute>} />
                                            <Route path="/roles" element={<ProtectedRoute allowedRoles={[Role.Manager, Role.Admin]} allowedDeviceTypes={BASE_ONLY_DEVICE_TYPES}><MainLayout><RolesPermissionsPage /></MainLayout></ProtectedRoute>} />
                                            <Route path="/platform-ops" element={<ProtectedRoute allowedRoles={[Role.Admin]} allowedDeviceTypes={BASE_ONLY_DEVICE_TYPES}><MainLayout><PlatformOpsPage /></MainLayout></ProtectedRoute>} />
                                            <Route path="/quick-start" element={<ProtectedRoute allowedRoles={[Role.Manager, Role.Admin]} allowedDeviceTypes={BASE_ONLY_DEVICE_TYPES}><MainLayout><QuickStartSetupPage /></MainLayout></ProtectedRoute>} />
                                            <Route path="/analytics" element={<ProtectedRoute allowedRoles={[Role.Manager, Role.Admin]} allowedDeviceTypes={BASE_ONLY_DEVICE_TYPES} requiredModules={['analytics']} requiredPermission={{ module: 'Administration', action: 'ExportOrFinance' }}><MainLayout><ClinicalAnalyticsPage /></MainLayout></ProtectedRoute>} />
                                            <Route path="/documents" element={<ProtectedRoute><MainLayout><DocumentsPage /></MainLayout></ProtectedRoute>} />
                                            <Route path="/trust" element={<ProtectedRoute requiredModules={['trustCenter']}><MainLayout><TrustCenterPage /></MainLayout></ProtectedRoute>} />
                                            <Route path="/cpd" element={<ProtectedRoute allowedDeviceTypes={PERSONAL_DEVICE_TYPES} requiredModules={['coreOps']}><MainLayout><CPDPage /></MainLayout></ProtectedRoute>} />
                                            <Route path="/wellbeing" element={<ProtectedRoute allowedDeviceTypes={PERSONAL_DEVICE_TYPES} requiredModules={['coreOps']}><MainLayout><WellbeingPage /></MainLayout></ProtectedRoute>} />
                                            <Route path="/profile" element={<ProtectedRoute><MainLayout><ProfilePage /></MainLayout></ProtectedRoute>} />
                                            <Route path="/major-incident" element={<ProtectedRoute allowedDeviceTypes={OPS_DEVICE_TYPES} requiredModules={['majorIncident']}><MainLayout><MajorIncidentPage /></MainLayout></ProtectedRoute>} />
                                            <Route path="/calculators" element={<ProtectedRoute><MainLayout><CalculatorsPage /></MainLayout></ProtectedRoute>} />
                                            <Route path="/guidelines" element={<ProtectedRoute allowedDeviceTypes={PERSONAL_DEVICE_TYPES} requiredModules={['coreOps']}><MainLayout><GuidelineAssistant /></MainLayout></ProtectedRoute>} />
                                            <Route path="/device-setup" element={<ProtectedRoute allowedRoles={[Role.Manager, Role.Admin]}><DeviceSetupPage /></ProtectedRoute>} />
                                            <Route path="/handover" element={<ProtectedRoute allowedDeviceTypes={OPS_DEVICE_TYPES} requiredModules={['clinicalOps']}><MainLayout><HospitalHandoverPage /></MainLayout></ProtectedRoute>} />
                                            <Route path="/portal/handover/:tokenId" element={<HospitalPortalView />} />
                                            <Route path="*" element={<Navigate to="/" replace />} />
                                        </Routes>
                                    </Suspense>
                                    </CrewProvider>
                                    </DeviceUnlockProvider>
                                </DeviceProvider>
                            </DataSyncProvider>
                        </ToastProvider>
                    </TenantProvider>
                </AuthProvider>
            </ThemeProvider>
        </Router>
    );

};

export default App;
